import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, X, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { CATEGORIES } from "@/lib/data";

const empty = {
  category: "disiplin",
  title: "",
  pages: 200,
  description: "",
  shopier_url: "https://www.shopier.com/stenot",
  active: true,
};

export default function AdminBooks() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    const { data } = await api.get("/admin/books");
    setItems(data.items || []);
  };

  useEffect(() => {
    if (!localStorage.getItem("life101_admin_token")) { nav("/admin/login"); return; }
    load();
  }, [nav]);

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Başlık ve açıklama zorunlu");
      return;
    }
    const payload = { ...form, pages: Number(form.pages) || 0 };
    try {
      if (editing) {
        await api.put(`/admin/books/${editing}`, payload);
        toast.success("Kitap güncellendi");
      } else {
        await api.post("/admin/books", payload);
        toast.success("Kitap eklendi");
      }
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setForm({
      category: b.category, title: b.title, pages: b.pages,
      description: b.description, shopier_url: b.shopier_url, active: b.active,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kitabı silmek istediğine emin misin?")) return;
    await api.delete(`/admin/books/${id}`);
    toast.success("Silindi");
    load();
  };

  const uploadCover = async (bid, file) => {
    if (!file) return;
    setUploadingId(bid);
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("life101_admin_token");
    try {
      const res = await fetch(`${API}/admin/books/${bid}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Yükleme başarısız");
      }
      toast.success("Kapak yüklendi");
      load();
    } catch (e) {
      toast.error(e.message || "Yükleme başarısız");
    } finally {
      setUploadingId(null);
    }
  };

  const removeCover = async (bid) => {
    if (!window.confirm("Kapak görselini kaldırmak istediğine emin misin?")) return;
    await api.delete(`/admin/books/${bid}/upload`);
    toast.success("Kapak kaldırıldı");
    load();
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#050505" }} data-testid="admin-books-page">
      <header className="max-w-7xl mx-auto px-6 py-7 flex items-center justify-between">
        <Link to="/admin" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={16} /> Dashboard</Link>
        <h1 className="text-xl font-semibold">Kitap Yönetimi</h1>
      </header>

      <main className="max-w-7xl mx-auto px-6 pb-16 space-y-8">
        <div className="glass-solid p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold">{editing ? "Kitabı Düzenle" : "Yeni Kitap Ekle"}</h2>
            {editing && (
              <button onClick={() => { setEditing(null); setForm(empty); }} className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm">
                <X size={14} /> İptal
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Kategori</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-white py-3 outline-none focus:border-white" data-testid="book-category">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-black">{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Başlık</label>
              <input className="life-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn: Disiplin 101" data-testid="book-title" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Sayfa Sayısı</label>
              <input type="number" className="life-input" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} data-testid="book-pages" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Shopier URL</label>
              <input className="life-input" value={form.shopier_url} onChange={(e) => setForm({ ...form, shopier_url: e.target.value })} placeholder="https://www.shopier.com/..." data-testid="book-url" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Açıklama</label>
              <textarea rows={3} className="w-full bg-transparent border-b border-white/10 text-white py-3 outline-none focus:border-white resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="book-description" />
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif (sitede görünür)
            </label>
          </div>
          <button onClick={save} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="book-save">
            {editing ? <><Save size={16} /> Güncelle</> : <><Plus size={16} /> Ekle</>}
          </button>
        </div>

        <div className="glass-solid overflow-x-auto">
          <table className="w-full text-sm" data-testid="books-table">
            <thead>
              <tr className="text-left text-neutral-500 text-xs uppercase tracking-[0.12em]">
                <th className="p-4">Sıra</th>
                <th className="p-4">Kategori</th>
                <th className="p-4">Kitap</th>
                <th className="p-4">Kapak</th>
                <th className="p-4">Sayfa</th>
                <th className="p-4">Shopier</th>
                <th className="p-4">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-t border-white/5 align-top" data-testid={`book-row-${b.id}`}>
                  <td className="p-4 text-neutral-500">{b.order}</td>
                  <td className="p-4 text-neutral-300">{b.category_label}</td>
                  <td className="p-4">
                    <div className="text-neutral-100">{b.title}</div>
                    <div className="text-neutral-500 text-xs mt-1 max-w-md">{b.description}</div>
                  </td>
                  <td className="p-4">
                    {b.cover_path ? (
                      <div className="text-xs text-emerald-400">Yüklü</div>
                    ) : (
                      <div className="text-xs text-neutral-500 inline-flex items-center gap-1"><ImageOff size={12} /> Yok</div>
                    )}
                    <input
                      ref={(el) => (fileRefs.current[b.id] = el)}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => uploadCover(b.id, e.target.files?.[0])}
                      data-testid={`book-cover-input-${b.id}`}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => fileRefs.current[b.id]?.click()}
                        disabled={uploadingId === b.id}
                        className="text-xs text-neutral-300 hover:text-white inline-flex items-center gap-1"
                        data-testid={`upload-cover-${b.id}`}
                      >
                        <Upload size={12} /> {uploadingId === b.id ? "..." : (b.cover_path ? "Değiştir" : "Yükle")}
                      </button>
                      {b.cover_path && (
                        <button onClick={() => removeCover(b.id)} className="text-xs text-red-400 hover:text-red-300" data-testid={`remove-cover-${b.id}`}>
                          Kaldır
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-neutral-400">{b.pages}</td>
                  <td className="p-4 text-neutral-500 max-w-[160px] truncate">
                    <a href={b.shopier_url} target="_blank" rel="noreferrer" className="hover:text-white underline">Aç</a>
                  </td>
                  <td className="p-4">{b.active ? <span className="text-white">Aktif</span> : <span className="text-neutral-500">Pasif</span>}</td>
                  <td className="p-4 text-right space-x-3 whitespace-nowrap">
                    <button onClick={() => startEdit(b)} className="text-neutral-300 hover:text-white text-xs" data-testid={`edit-book-${b.id}`}>Düzenle</button>
                    <button onClick={() => remove(b.id)} className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 text-xs" data-testid={`delete-book-${b.id}`}><Trash2 size={12} /> Sil</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-neutral-500">Henüz kitap yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
